import {
  ModuleDefinitionSchema,
  UpdateFormModuleSchema,
  isBoundField,
  type BoundField,
  type FormModuleDetail,
  type FormModuleChange,
  type ModuleDefinitionField,
} from "shared"
import type { FieldEdit } from "@rawa/form-modules/inspectors"
import { moveItem } from "../utils/moveItem"

/** A module's name and description, as the canvas edits them. */
export interface ModuleDetails {
  name: string
  description: string
}

/**
 * What can change on a bound field: its name, without which it is called what
 * it is bound to, and whether it is Required, where its binding leaves that to
 * the administrator.
 */
export type BoundFieldEdit = Partial<Pick<BoundField, "label" | "required">>

/** What a save sends from the canvas: everything on it when Save was pressed. */
export interface ModuleEdit {
  fields: ModuleDefinitionField[]
  details: ModuleDetails
}

/**
 * The administrator's working copy of a module: its name, description and
 * fields as edited and as the server holds them, the revision they are stored
 * at, which a save names as the one it was edited from, the version it has
 * published (null before the first publish), what publishing what is saved
 * would do, as the server last said (ADR-040), and which field's properties
 * are open, null when none are. The selection always names a held field,
 * because the events that part with one close it.
 */
export interface ModuleState {
  details: ModuleDetails
  storedDetails: ModuleDetails
  fields: ModuleDefinitionField[]
  stored: ModuleDefinitionField[]
  revision: number
  publishedVersion: number | null
  unpublishedChange: FormModuleChange | null
  selectedId: string | null
}

/** Everything that can happen to the module as edited, each applied whole. */
export type ModuleEvent =
  | { type: "renamed"; name: string }
  | { type: "described"; description: string }
  | { type: "added"; field: ModuleDefinitionField; at: number }
  | { type: "moved"; index: number; by: number }
  | { type: "edited"; id: string; edit: FieldEdit }
  | { type: "boundFieldEdited"; id: string; edit: BoundFieldEdit }
  | { type: "removed"; id: string }
  | { type: "selected"; id: string }
  | { type: "deselected" }
  | { type: "saved"; sent: ModuleEdit; module: FormModuleDetail }
  | { type: "reloaded"; module: FormModuleDetail }
  | { type: "published"; module: FormModuleDetail }

const detailsOf = ({ name, description }: FormModuleDetail): ModuleDetails => ({
  name,
  description,
})

export const seedModuleState = (module: FormModuleDetail): ModuleState => ({
  details: detailsOf(module),
  storedDetails: detailsOf(module),
  fields: module.definition.fields,
  stored: module.definition.fields,
  revision: module.revision,
  publishedVersion: module.publishedVersion,
  unpublishedChange: module.unpublishedChange,
  selectedId: null,
})

/**
 * Whether there is a saved change to publish, and nothing unsaved: a publish
 * takes what is saved, so an unsaved change would silently be left out.
 */
export const canPublish = (state: ModuleState): boolean =>
  state.unpublishedChange !== null && !hasUnsavedChanges(state)

/** Whether the canvas differs from what the server holds. */
export const hasUnsavedChanges = (state: ModuleState): boolean =>
  !sameValue(asStored(state.fields), asStored(state.stored)) ||
  !sameValue(detailsAsStored(state.details), state.storedDetails)

/** Whether the server would accept the canvas as it stands. */
export const canSave = (state: ModuleState): boolean =>
  checkedFields(state.fields).success && hasValidDetails(state)

/** Whether the server would accept the name and description as they stand. */
export const hasValidDetails = (state: ModuleState): boolean =>
  checkedDetails(state.details).success

/** The fields whose problems hold Save, named for the canvas to mark. */
export const fieldsNeedingAttention = (state: ModuleState): Set<string> =>
  new Set(
    (checkedFields(state.fields).error?.issues ?? []).flatMap(
      ({ path: [list, index] }) =>
        list === "fields" && typeof index === "number"
          ? [state.fields[index].id]
          : [],
    ),
  )

// The same rules the server applies on save, parsed once per fields value:
// both selectors above read one outcome, and a render that changes nothing
// else reads it again for free.
const checks = new WeakMap<
  ModuleDefinitionField[],
  ReturnType<typeof ModuleDefinitionSchema.safeParse>
>()
const checkedFields = (fields: ModuleDefinitionField[]) => {
  const held = checks.get(fields)
  if (held) return held
  const outcome = ModuleDefinitionSchema.safeParse({ fields })
  checks.set(fields, outcome)
  return outcome
}

// The fields as the server would store them: as the schema parses them, with
// names trimmed, when it would take them; as they are when it would not, which
// differs from anything stored.
const asStored = (fields: ModuleDefinitionField[]) => {
  const checked = checkedFields(fields)
  return checked.success ? checked.data.fields : fields
}

// The name and description, by the same schema and the same reasoning.
const DetailsSchema = UpdateFormModuleSchema.pick({
  name: true,
  description: true,
})
const detailChecks = new WeakMap<
  ModuleDetails,
  ReturnType<typeof DetailsSchema.safeParse>
>()
const checkedDetails = (details: ModuleDetails) => {
  const held = detailChecks.get(details)
  if (held) return held
  const outcome = DetailsSchema.safeParse(details)
  detailChecks.set(details, outcome)
  return outcome
}
const detailsAsStored = (details: ModuleDetails) => {
  const checked = checkedDetails(details)
  return checked.success ? checked.data : details
}

// A key set to nothing is no key at all, as on the wire.
const definedKeys = (value: object) =>
  Object.keys(value).filter(
    (key) => (value as Record<string, unknown>)[key] !== undefined,
  )

const sameValue = (a: unknown, b: unknown): boolean => {
  if (a === b) return true
  if (Array.isArray(a) && Array.isArray(b))
    return a.length === b.length && a.every((held, i) => sameValue(held, b[i]))
  if (a && b && typeof a === "object" && typeof b === "object") {
    const keys = definedKeys(a)
    return (
      keys.length === definedKeys(b).length &&
      keys.every((key) =>
        sameValue(
          (a as Record<string, unknown>)[key],
          (b as Record<string, unknown>)[key],
        ),
      )
    )
  }
  return false
}

const removeField = (
  fields: ModuleDefinitionField[],
  id: string,
): ModuleDefinitionField[] => fields.filter((field) => field.id !== id)

const changeField = (
  fields: ModuleDefinitionField[],
  id: string,
  edit: FieldEdit,
): ModuleDefinitionField[] =>
  fields.map((field) =>
    field.id === id && !isBoundField(field) ? { ...field, ...edit } : field,
  )

const editBoundField = (
  fields: ModuleDefinitionField[],
  id: string,
  edit: BoundFieldEdit,
): ModuleDefinitionField[] =>
  fields.map((field) =>
    field.id === id && isBoundField(field) ? { ...field, ...edit } : field,
  )

/**
 * Selecting toggles, as the canvas rows do. On a save, the server's copy
 * becomes the stored, and replaces each part of the canvas — the details, the
 * fields — only when that part still holds exactly what was sent, so an edit
 * made while the save was in flight stays.
 */
export function moduleReducer(
  state: ModuleState,
  event: ModuleEvent,
): ModuleState {
  switch (event.type) {
    case "renamed":
      return { ...state, details: { ...state.details, name: event.name } }
    case "described":
      return {
        ...state,
        details: { ...state.details, description: event.description },
      }
    case "added":
      return {
        ...state,
        fields: [
          ...state.fields.slice(0, event.at),
          event.field,
          ...state.fields.slice(event.at),
        ],
        selectedId: event.field.id,
      }
    case "moved":
      return {
        ...state,
        fields: moveItem(state.fields, event.index, event.by),
      }
    case "edited":
      return {
        ...state,
        fields: changeField(state.fields, event.id, event.edit),
      }
    case "boundFieldEdited":
      return {
        ...state,
        fields: editBoundField(state.fields, event.id, event.edit),
      }
    case "removed":
      return {
        ...state,
        fields: removeField(state.fields, event.id),
        selectedId: state.selectedId === event.id ? null : state.selectedId,
      }
    case "selected":
      return {
        ...state,
        selectedId: state.selectedId === event.id ? null : event.id,
      }
    case "deselected":
      return { ...state, selectedId: null }
    case "saved": {
      const stored = seedModuleState(event.module)
      return {
        ...state,
        details:
          state.details === event.sent.details ? stored.details : state.details,
        storedDetails: stored.storedDetails,
        fields:
          state.fields === event.sent.fields ? stored.fields : state.fields,
        stored: stored.stored,
        revision: stored.revision,
        publishedVersion: stored.publishedVersion,
        unpublishedChange: stored.unpublishedChange,
      }
    }
    case "reloaded":
      return seedModuleState(event.module)
    // A publish takes only what is saved, so the canvas already holds what the
    // module now publishes; what changes is its version and what is waiting.
    case "published":
      return {
        ...state,
        publishedVersion: event.module.publishedVersion,
        unpublishedChange: event.module.unpublishedChange,
      }
  }
}
